import { useMemo, useState } from "react";
import { bool, string, object, number } from "prop-types";
import { observer } from "mobx-react";
import { Tables, hashCode, DescriptionModes, BreakdownTableDepths } from "@repo/utils";
import {
  useEstimateStructure,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  useTableExpandHandler,
  Row,
} from "@tools";
import { DescriptionRow } from "../DescriptionRow/DescriptionRow";
import { ColumnFactory } from "./ColumnFactory";
import { BreakdownRowGroupContainer } from "./BreakdownRowGroupContainer";
import { ExpandContainer, NumerationContainer } from "@tools/EstimateEditor/components/Tables/cells/PinnableCell/components";
import { Collapse } from "@material-ui/core";

export const BreakdownRowGroup = observer(({
  element,
  isLastGroupRow,
  parentName,
  tableDepth = BreakdownTableDepths.SECTION,
  initialDepth = BreakdownTableDepths.SECTION,
  isFirst,
  ...containerProps
}) => {
  const structure = useEstimateStructure();
  
  const {
    allowEdition,
    useVisibilityCell,
    blockExpansions,
    forceExpansionArrows,
    displayLibraryElementStatus,
  } = useEstimateEditorSettings();
  
  const {
    useExpandCell,
    showNumerationColumn,
    descriptionColumns,
  } = useEstimateTableSettings();
  
  const [ descEdition, setDescEdition ] = useState(undefined);

  const { estimateViewLevel } = structure;
  const { descriptionMode, showBreakdownChildCounters } = structure.settings;

  const {
    treePath,
    isTurnedOff,
    hasDisplayChildren,
    displayOpened,
    status,
    hasAnyDescriptions,
  } = element;

  const actualDepthLevel = useMemo(() => (
    tableDepth + initialDepth
  ), [tableDepth, initialDepth]);

  const expandable = useMemo(() => (
    actualDepthLevel < estimateViewLevel && (allowEdition || hasDisplayChildren)
  ), [ actualDepthLevel, estimateViewLevel, allowEdition, hasDisplayChildren ]);
  
  const hasEditedDescription = useMemo(() => (
    descriptionMode === DescriptionModes.ROW ? hasAnyDescriptions || descEdition : descEdition
  ), [ descEdition, descriptionMode, hasAnyDescriptions ]);
  
  const isAcceptedComponent = useMemo(() => (
    status === 1 && displayLibraryElementStatus
  ), [status, displayLibraryElementStatus]);

  const expanded = useMemo(() => (
    isAcceptedComponent || (expandable && (displayOpened || (blockExpansions && !forceExpansionArrows)))
  ), [ isAcceptedComponent, expandable, displayOpened, blockExpansions ]);

  const isSectionRow = actualDepthLevel === BreakdownTableDepths.SECTION;
  
  const hashedPath = useMemo(() => (
    [Tables.BREAKDOWN, ...treePath].map((id) => hashCode(id))
  ), [treePath]);

  const handleDescriptionEdition = () => {
    setDescEdition(!descEdition);
  }

  const confirmDescriptionEdition = () => {
    setDescEdition(false);
  }

  const handleTurnOff = () => {
    element.setTurnOffState(!isTurnedOff, true);
  };
  const handleShowMoreDescription = () => {
    element.expandAllDescriptionColumns();
  }

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: expandable && !blockExpansions,
    isOpened: displayOpened,
    onExpand: element.setOpenState,
    isEmpty: descriptionMode === DescriptionModes.COLUMN && hasAnyDescriptions && !blockExpansions && !(hasDisplayChildren || (allowEdition && actualDepthLevel < estimateViewLevel)),
    onEmptyExpand: handleShowMoreDescription,
  });
  
  return (
    <BreakdownRowGroupContainer
      tableDepth={tableDepth}
      hashedPath={hashedPath}
      actualDepthLevel={actualDepthLevel}
      collapseProps={collapseProps}
      element={element}
      initialDepth={initialDepth}
      isFirst={isFirst}
      isLastGroupRow={isLastGroupRow}
      expanded={expanded}
      expandable={expandable}
      isSectionRow={isSectionRow}
      hasEditedDescription={hasEditedDescription}
      { ...containerProps }
    >
      <Row
        // useExpandCell={useExpandCell}
        useDragHandleCell={allowEdition}
        useVisibilityCell={useVisibilityCell}
        expandOnClick
        expandable={expandable}
        expanded={expanded}
        onExpand={handleOpen}
        visible={!isTurnedOff}
        onVisibilityToggle={handleTurnOff}
        isFirstGroupRow={tableDepth <= BreakdownTableDepths.MODULE}
        isLastGroupRow={
          isSectionRow || (isLastGroupRow && !expanded && !hasEditedDescription)
        }
        // highlightCellText={isAcceptedComponent}
        tableDepth={tableDepth}
        style={!tableDepth ? { marginBottom: 2 } : undefined}
        actionPresetClass="preset-page"
      >
        <ColumnFactory
          isSectionRow={isSectionRow}
          element={element}
          parentName={parentName}
          actualDepthLevel={actualDepthLevel}
          expanded={expanded}
          tableDepth={tableDepth}
          hashedPath={hashedPath}
          handleDescriptionEdition={handleDescriptionEdition}
          confirmDescriptionEdition={confirmDescriptionEdition}
        >
          {
            useExpandCell &&
            <ExpandContainer
              tableDepth={tableDepth}
              expandable={expandable && !forceExpansionArrows}
              expanded={expanded}
              // onExpand={handleOpen}
              noArrow={actualDepthLevel === BreakdownTableDepths.MODULE && !forceExpansionArrows}
              presetClassName="preset-breakdownExpandButton"
              childrenAmount={element.children?.length}
              showBadge={showBreakdownChildCounters && !expanded && actualDepthLevel < estimateViewLevel}
            />
          }
          {
            showNumerationColumn &&
            <NumerationContainer element={element} />
          }
        </ColumnFactory>
      </Row>
      {
        descriptionMode !== DescriptionModes.COLUMN &&
        <Collapse
          in={hasEditedDescription}
          mountOnEnter
          unmountOnExit
          classes={{ root: "noDrag w-full" }}
        >
          {/*<DescriptionRow*/}
          {/*  element={element}*/}
          {/*  isLastGroupRow={isLastGroupRow && !expanded && !additionalDescriptionColumnsColumns.length}*/}
          {/*  confirmDescriptionEdition={confirmDescriptionEdition}*/}
          {/*  tableDepth={tableDepth}*/}
          {/*  staticDescription={descriptionMode === DescriptionModes.ROW}*/}
          {/*  isSectionRow={isSectionRow}*/}
          {/*/>*/}
          {
            descriptionColumns
              .map((column, index, l) => (
                <DescriptionRow
                  key={column.descriptionId}
                  element={element}
                  allowAutoFocus={descEdition && l.length === 1}
                  columnSettings={column}
                  isLastGroupRow={isLastGroupRow && !expanded && index === l.length - 1}
                  onAfterChange={confirmDescriptionEdition}
                  tableDepth={tableDepth}
                  staticDescription={descriptionMode === DescriptionModes.ROW}
                  isSectionRow={isSectionRow}
                  showHeader={l.length > 1}
                />
              ))
          }
        </Collapse>
      }
    </BreakdownRowGroupContainer>
  );
});

BreakdownRowGroup.propTypes = {
  element: object.isRequired,
  tableDepth: number,
  initialDepth: number,
  parentName: string,
  isFirst: bool,
  isLastGroupRow: bool,
  isExpanding: bool,
  index: number,
};
